import { Link, useRouterState } from "@tanstack/react-router";
import {
  Search,
  ShoppingCart,
  Menu,
  User,
  Package,
  MapPin,
  LogOut,
  LogIn,
  UserPlus,
  Truck,
} from "lucide-react";
import { NavSearch } from "@/components/shop/NavSearch";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import logoAsset from "@/assets/fixhen-logo.png.asset.json";
import { useCart, selectCount } from "@/lib/cart/cart-store";

type NavItem = { label: string; to: string; search?: Record<string, unknown> };
const nav: NavItem[] = [
  { label: "Shop", to: "/shop" },
  { label: "Blog", to: "/blog" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const MODEL_SHORTCUTS: { id: string; label: string; brand: "mercedes" | "bmw" }[] = [
  { id: "r129", label: "R129", brand: "mercedes" },
  { id: "w124", label: "W124", brand: "mercedes" },
  { id: "w201", label: "W201", brand: "mercedes" },
  { id: "w208", label: "W208", brand: "mercedes" },
  { id: "e30", label: "E30", brand: "bmw" },
  { id: "e34", label: "E34", brand: "bmw" },
  { id: "e36", label: "E36", brand: "bmw" },
  { id: "e46", label: "E46", brand: "bmw" },
];

export function SiteHeader() {
  const [email, setEmail] = useState<string | null>(null);
  // Zustand's `persist` rehydrates from localStorage synchronously on mount,
  // so the cart count on the client can differ from SSR (which has no
  // localStorage). Rendering that difference during hydration triggers
  // React #419 inside pages that also use Suspense. Defer the badge to a
  // post-hydration effect so SSR HTML always matches first client render.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const qc = useQueryClient();
  const cartCount = useCart(selectCount);
  const openCart = useCart((s) => s.open);

  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const path = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    setMenuOpen(false);
    setMobileSearchOpen(false);
  }, [path]);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getUser().then(({ data }) => {
      if (mounted) setEmail(data.user?.email ?? null);
    });
    const sub = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
        setEmail(session?.user?.email ?? null);
      }
    });
    return () => {
      mounted = false;
      sub.data.subscription.unsubscribe();
    };
  }, []);

  async function signOut() {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    setMenuOpen(false);
  }

  const initials = email ? email[0].toUpperCase() : "";

  return (
    <header className="sticky top-7 z-40 border-b border-border/60 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70 sm:top-8">
      <div className="mx-auto flex h-12 max-w-7xl items-center gap-2 px-3 sm:h-16 sm:gap-3 sm:px-6">
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          className="press grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-border sm:h-11 sm:w-11 md:hidden"
          aria-label="Open menu"
        >
          <Menu className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
        <Link to="/" className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <img src={logoAsset.url} alt="FIXHEN logo" width={36} height={36} className="h-7 w-7 sm:h-9 sm:w-9" />
          <span className="text-base font-black tracking-tight text-ink sm:text-xl">FIXHEN</span>
        </Link>
        <nav className="ml-6 hidden items-center gap-1 md:flex">
          {nav.map((n) => (
            <Link
              key={n.label}
              to={n.to as never}
              search={(n.search ?? {}) as never}
              className="rounded-lg px-3 py-2 text-sm font-medium text-ink-soft transition-colors hover:bg-secondary hover:text-ink"
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <div className="hidden w-72 md:block">
            <NavSearch />
          </div>
          <button
            type="button"
            onClick={() => setMobileSearchOpen(true)}
            aria-label="Search"
            className="press grid h-9 w-9 place-items-center rounded-xl border border-border sm:h-11 sm:w-11 md:hidden"
          >
            <Search className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label="Account"
              className="press grid h-9 w-9 place-items-center rounded-xl border border-border sm:h-11 sm:w-11"
            >
              {email ? (
                <div className="grid h-6 w-6 place-items-center rounded-full bg-[#172554] text-[10px] font-bold text-white sm:h-7 sm:w-7 sm:text-xs">
                  {initials}
                </div>
              ) : (
                <User className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {email ? (
                <>
                  <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">
                    {email}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={"/account" as never}>
                      <User className="mr-2 h-4 w-4" /> Account overview
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to={"/account/orders" as never}>
                      <Package className="mr-2 h-4 w-4" /> My orders
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to={"/account/addresses" as never}>
                      <MapPin className="mr-2 h-4 w-4" /> Addresses
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={signOut} className="text-rose-600 focus:text-rose-600">
                    <LogOut className="mr-2 h-4 w-4" /> Sign out
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem asChild>
                    <Link to={"/auth" as never} search={{ mode: "signin" } as never}>
                      <LogIn className="mr-2 h-4 w-4" /> Sign in
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to={"/auth" as never} search={{ mode: "signup" } as never}>
                      <UserPlus className="mr-2 h-4 w-4" /> Create account
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            aria-label="Cart"
            onClick={() => openCart()}
            className="press relative grid h-9 w-9 place-items-center rounded-xl bg-ink text-paper sm:h-11 sm:w-11"
          >
            <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5" />
            {mounted && cartCount > 0 && (
              <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-sky-pop px-1 text-[9px] font-bold text-ink sm:h-5 sm:min-w-5 sm:text-[10px]">
                {cartCount > 99 ? "99+" : cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* MOBILE NAV DRAWER */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-[88vw] max-w-sm overflow-y-auto p-0">
          <SheetHeader className="border-b border-border px-5 py-4">
            <SheetTitle className="flex items-center gap-2 text-left">
              <img src={logoAsset.url} alt="" width={28} height={28} className="h-7 w-7" />
              <span className="text-lg font-black tracking-tight text-ink">FIXHEN</span>
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-col gap-6 px-5 py-5">
            <nav className="flex flex-col">
              {nav.map((n) => (
                <Link
                  key={n.label}
                  to={n.to as never}
                  search={(n.search ?? {}) as never}
                  onClick={() => setMenuOpen(false)}
                  className="press flex items-center justify-between rounded-xl px-3 py-3 text-base font-bold text-ink hover:bg-secondary"
                >
                  {n.label}
                </Link>
              ))}
            </nav>

            <div>
              <p className="mb-2 px-3 text-[11px] font-bold uppercase tracking-widest text-royal">
                Jump to a model
              </p>
              <div className="flex flex-wrap gap-2 px-1">
                {MODEL_SHORTCUTS.map((m) => (
                  <Link
                    key={m.id}
                    to="/shop"
                    search={{ brand: m.brand, models: [m.id] } as never}
                    onClick={() => setMenuOpen(false)}
                    className="press rounded-full border border-border bg-secondary/50 px-3 py-1.5 text-xs font-bold text-ink hover:bg-secondary"
                  >
                    {m.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-secondary/40 p-3">
              {email ? (
                <>
                  <div className="mb-2 flex items-center gap-2 px-1">
                    <div className="grid h-8 w-8 place-items-center rounded-full bg-[#172554] text-xs font-bold text-white">
                      {initials}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">{email}</p>
                  </div>
                  <Link
                    to={"/account" as never}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-ink hover:bg-background"
                  >
                    <User className="h-4 w-4" /> Account overview
                  </Link>
                  <Link
                    to={"/account/orders" as never}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-ink hover:bg-background"
                  >
                    <Package className="h-4 w-4" /> My orders
                  </Link>
                  <Link
                    to={"/account/addresses" as never}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-ink hover:bg-background"
                  >
                    <MapPin className="h-4 w-4" /> Addresses
                  </Link>
                  <button
                    type="button"
                    onClick={signOut}
                    className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-rose-600 hover:bg-background"
                  >
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-2">
                  <Link
                    to={"/auth" as never}
                    search={{ mode: "signin" } as never}
                    onClick={() => setMenuOpen(false)}
                    className="press inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-ink text-sm font-bold text-paper"
                  >
                    <LogIn className="h-4 w-4" /> Sign in
                  </Link>
                  <Link
                    to={"/auth" as never}
                    search={{ mode: "signup" } as never}
                    onClick={() => setMenuOpen(false)}
                    className="press inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-border bg-background text-sm font-bold text-ink"
                  >
                    <UserPlus className="h-4 w-4" /> Create account
                  </Link>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 rounded-xl bg-ink px-3 py-2.5 text-xs font-medium text-paper">
              <Truck className="h-4 w-4 text-sky-pop" />
              Free shipping over €99 · worldwide tracked delivery
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* MOBILE SEARCH SHEET */}
      <Sheet open={mobileSearchOpen} onOpenChange={setMobileSearchOpen}>
        <SheetContent side="top" className="p-5">
          <SheetHeader>
            <SheetTitle className="text-left text-sm font-bold uppercase tracking-widest text-royal">
              Search parts
            </SheetTitle>
          </SheetHeader>
          <div className="mt-3">
            <NavSearch
              autoFocus
              onNavigate={() => setMobileSearchOpen(false)}
              inputClassName="h-12 w-full rounded-xl border border-border bg-background pl-9 pr-10 text-base text-ink outline-none focus:border-ring"
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Type a part name, SKU or OEM number — suggestions appear as you type.
          </p>
        </SheetContent>
      </Sheet>
    </header>
  );
}